import createClient, { type Middleware } from "openapi-fetch";
import type { paths } from "@hamlak/api-spec";
import { platform } from "@/platform";
import { useAuthStore } from "@/stores/authStore";
import { t } from "@/i18n/ko";
import { ApiError, applyClientHeaders, authMode, handleCommonError, refreshOnce, toApiError, toUrl } from "./client";

/**
 * openapi-fetch 클라이언트. 경로·파라미터·본문·응답 타입은 @hamlak/api-spec `paths`에서만 온다.
 * 기존 apiFetch(client.ts)의 파이프라인(X-Client · Bearer/Cookie 분기 · 401 refresh 단일 비행 · 0.5절 공통 오류 분기)을
 * 미들웨어로 같은 함수(authMode·refreshOnce·toApiError·handleCommonError)에 얹는다. 두 경로는 같은 refresh 비행을 공유한다.
 *
 * 사용: `const tx = await call(api.GET("/me/bricks/transactions", { params: { query: { type: "usage" } } }))`.
 * 실패는 apiFetch와 같은 ApiError로 throw(네트워크 → NETWORK). 성공 data만 돌려준다.
 */

/** 재시도용 복제본(요청 본문은 한 번만 읽힌다). 요청 id → Request */
const retryable = new Map<string, Request>();

async function withAuth(request: Request): Promise<Request> {
  await applyClientHeaders(request.headers);
  if (authMode() === "bearer") {
    const at = useAuthStore.getState().accessToken;
    if (at) request.headers.set("Authorization", `Bearer ${at}`);
    else request.headers.delete("Authorization");
  }
  return request;
}

const pipeline: Middleware = {
  onRequest({ request, id }) {
    retryable.set(id, request.clone());
    return withAuth(request);
  },
  async onResponse({ response, id, options }) {
    const clone = retryable.get(id);
    retryable.delete(id);
    let res = response;
    if (res.status === 401 && clone) {
      await refreshOnce(); // 실패하면 로그아웃 후 throw(apiFetch와 동일)
      res = await options.fetch(await withAuth(clone));
    }
    if (res.ok) return res;
    const err = await toApiError(res);
    handleCommonError(err);
    throw err;
  },
  onError({ error, id }) {
    retryable.delete(id);
    if (error instanceof DOMException && error.name === "AbortError") return error;
    if (error instanceof ApiError) return error;
    return new ApiError(0, "NETWORK", t("error.network"), error);
  },
};

export const api = createClient<paths>({
  baseUrl: toUrl("/api"),
  // 호출 시점의 globalThis.fetch(테스트 MSW가 listen() 때 바꿔 끼운다).
  fetch: (input) => globalThis.fetch(input),
});
api.use(pipeline);

/** 계약상 모든 요청의 header 파라미터 `X-Client`(필수). 미들웨어도 같은 값을 다시 쓴다. */
export const xClient = () => ({ "X-Client": platform.clientKind });

type Result<T> = { data?: T; error?: unknown; response: Response };

/** openapi-fetch 결과 → data. 오류는 미들웨어가 이미 ApiError로 던졌으므로 여기 오면 성공이다. */
export async function call<T>(p: Promise<Result<T>>): Promise<T> {
  const r = await p;
  if (r.error !== undefined) throw new ApiError(r.response.status, "UNKNOWN", t("error.generic"), r.error);
  return r.data as T;
}
